import { $t } from '@/i18n';
import { defineComponent, ref, toRefs, computed, watch, inject, } from '@vue/composition-api';
import { useStorage } from '@vueuse/core';
import { AlignJustify, ChevronDown, ChevronLeft, ChevronRight, Eye, Folder, FolderTree, List, SquareSplitHorizontal, } from 'lucide-vue';
import { startMouseMove } from '@tencent/ui-core/lib/utils/browser';
import { getAIPageCodeByCommitId } from '@/modules/ai-page/utils/code';
import { DetailDiffPro } from './detailDiffPro';
import styles from './aiPageDiff.module.scss';
import { MERGE_AI_RE_RESOLVE_HANDLER_KEY } from '@/components/branch/merge-modal/constants';
// ==================== 类型定义 ====================
var FileChangeType;
(function (FileChangeType) {
    FileChangeType["Added"] = "added";
    FileChangeType["Deleted"] = "deleted";
    FileChangeType["Modified"] = "modified";
})(FileChangeType || (FileChangeType = {}));
var FileNodeType;
(function (FileNodeType) {
    FileNodeType["File"] = "file";
    FileNodeType["Directory"] = "directory";
})(FileNodeType || (FileNodeType = {}));
var ViewMode;
(function (ViewMode) {
    ViewMode["List"] = "list";
    ViewMode["Tree"] = "tree";
    ViewMode["Review"] = "review";
})(ViewMode || (ViewMode = {}));
/** Diff 展示模式：单栏/双栏 */
var DiffViewMode;
(function (DiffViewMode) {
    DiffViewMode["Unified"] = "unified";
    DiffViewMode["Split"] = "split";
})(DiffViewMode || (DiffViewMode = {}));
// ==================== 常量定义 ====================
const FILE_CHANGE_TYPE_ORDER = {
    [FileChangeType.Modified]: 0,
    [FileChangeType.Added]: 1,
    [FileChangeType.Deleted]: 2,
};
const DEFAULT_VIEW_MODE = ViewMode.List;
const VIEW_MODES = [ViewMode.List, ViewMode.Tree, ViewMode.Review];
const VIEW_MODE_STORAGE_KEY = 'wuji:aiPageDiff:viewMode';
const DEFAULT_DIFF_VIEW_MODE = DiffViewMode.Split;
const DIFF_VIEW_MODE_STORAGE_KEY = 'wuji:aiPageDiff:diffViewMode';
// 左侧文件列表宽度限制（可拖动调节）
const FILE_LIST_MIN_WIDTH = 200;
const FILE_LIST_MAX_WIDTH = 800;
const FILE_LIST_DEFAULT_WIDTH = 250;
const FILE_LIST_WIDTH_STORAGE_KEY = 'wuji:aiPageDiff:fileListWidth';
const FILE_LIST_COLLAPSED_STORAGE_KEY = 'wuji:aiPageDiff:fileListCollapsed';
const VIEW_MODE_LABEL = {
    [ViewMode.List]: $t('k_68b75749'),
    [ViewMode.Tree]: $t('k_c59ad37d'),
    [ViewMode.Review]: $t('k_89dfa6fb'),
};
const isViewMode = (value) => typeof value === 'string'
    && VIEW_MODES.includes(value);
// ==================== 工具函数 ====================
/**
 * 创建文件路径到文件节点的映射
 */
const createFileMap = (files, map = new Map()) => {
    files.forEach((file) => {
        map.set(file.path, file);
        if (file.type === FileNodeType.Directory && file.children) {
            createFileMap(file.children, map);
        }
    });
    return map;
};
/**
 * 对比两个文件树，找出所有变更
 */
const diffFileTrees = (originFiles, compareFilesTree) => {
    const changes = [];
    const originMap = createFileMap(originFiles);
    const compareMap = createFileMap(compareFilesTree);
    // 找出新增和修改的文件
    compareMap.forEach((compareFile, path) => {
        if (compareFile.type === FileNodeType.File) {
            const originFile = originMap.get(path);
            if (!originFile) {
                // 新增文件
                changes.push({
                    path,
                    name: compareFile.name,
                    type: FileChangeType.Added,
                    modifiedContent: compareFile.content || '',
                });
            }
            else if (originFile.oid !== compareFile.oid) {
                // 修改文件
                changes.push({
                    path,
                    name: compareFile.name,
                    type: FileChangeType.Modified,
                    originalContent: originFile.content || '',
                    modifiedContent: compareFile.content || '',
                });
            }
        }
    });
    // 找出删除的文件
    originMap.forEach((originFile, path) => {
        if (originFile.type === FileNodeType.File && !compareMap.has(path)) {
            changes.push({
                path,
                name: originFile.name,
                type: FileChangeType.Deleted,
                originalContent: originFile.content || '',
            });
        }
    });
    // 按修改、新增、删除的顺序排序，同类型内按路径排序
    return changes.sort((a, b) => {
        const typeComparison = FILE_CHANGE_TYPE_ORDER[a.type] - FILE_CHANGE_TYPE_ORDER[b.type];
        if (typeComparison !== 0) {
            return typeComparison;
        }
        return a.path.localeCompare(b.path);
    });
};
/**
 * 合并两侧文件树为统一树结构，只保留包含变更文件的分支
 */
const mergeSiblingNodes = (originItems, compareItems, fileChangeMap) => {
    const pairs = new Map();
    originItems.forEach((node) => {
        pairs.set(node.path, { ...pairs.get(node.path), o: node });
    });
    compareItems.forEach((node) => {
        pairs.set(node.path, { ...pairs.get(node.path), c: node });
    });
    const sortedPaths = Array.from(pairs.keys()).sort((a, b) => a.localeCompare(b));
    const out = [];
    sortedPaths.forEach((path) => {
        const { o, c } = pairs.get(path);
        const oIsDir = o?.type === FileNodeType.Directory;
        const cIsDir = c?.type === FileNodeType.Directory;
        const oIsFile = o?.type === FileNodeType.File;
        const cIsFile = c?.type === FileNodeType.File;
        if (oIsDir || cIsDir) {
            const oChildren = oIsDir ? o.children ?? [] : [];
            const cChildren = cIsDir ? c.children ?? [] : [];
            const children = mergeSiblingNodes(oChildren, cChildren, fileChangeMap);
            if (children.length === 0) {
                return;
            }
            const { name } = (c ?? o);
            out.push({
                kind: 'directory',
                path,
                name,
                children,
            });
            return;
        }
        if (oIsFile && cIsFile) {
            const change = fileChangeMap.get(path);
            if (!change) {
                return;
            }
            out.push({
                kind: 'file',
                path,
                name: c.name,
                change,
            });
            return;
        }
        if (oIsFile && !c) {
            const change = fileChangeMap.get(path);
            if (!change) {
                return;
            }
            out.push({
                kind: 'file',
                path,
                name: o.name,
                change,
            });
            return;
        }
        if (!o && cIsFile) {
            const change = fileChangeMap.get(path);
            if (!change) {
                return;
            }
            out.push({
                kind: 'file',
                path,
                name: c.name,
                change,
            });
        }
    });
    return out;
};
const mergeFileTrees = (originRoots, compareRoots, fileChangeMap) => mergeSiblingNodes(originRoots, compareRoots, fileChangeMap);
/**
 * 列表视图：固定 2 级，按目录路径分组
 */
const buildGroupedFileChanges = (changes) => {
    const map = new Map();
    changes.forEach((f) => {
        const dirKey = getDirectoryPath(f.path);
        if (!map.has(dirKey)) {
            map.set(dirKey, []);
        }
        map.get(dirKey).push(f);
    });
    return Array.from(map.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([dirKey, files]) => ({
        dirKey,
        dirLabel: dirKey === '/' ? '/' : dirKey.replace(/\/$/, ''),
        files: files.sort((a, b) => a.path.localeCompare(b.path)),
    }));
};
/**
 * 渲染文件变更类型图标
 */
const renderFileChangeIcon = (type) => {
    const iconConfig = {
        [FileChangeType.Added]: {
            label: 'A',
            className: 'bg-green-600/20 text-green-600',
        },
        [FileChangeType.Deleted]: {
            label: 'D',
            className: 'bg-red-600/20 text-red-600',
        },
        [FileChangeType.Modified]: {
            label: 'M',
            className: 'bg-blue-600/20 text-blue-600',
        },
    };
    const config = iconConfig[type];
    return (<span class={`inline-flex items-center justify-center w-4 h-4 rounded text-xs font-semibold ${config.className}`}>
      {config.label}
    </span>);
};
/**
 * 提取文件路径的目录部分（末尾保留 `/`，根路径文件归为 `'/'`）
 */
const getDirectoryPath = (filePath) => {
    const lastSlashIndex = filePath.lastIndexOf('/');
    return lastSlashIndex >= 0 ? filePath.substring(0, lastSlashIndex + 1) : '/';
};
/**
 * 根据文件名后缀获取 Monaco Editor 的语言类型
 */
const getLanguageFromFileName = (fileName) => {
    const extension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
    const languageMap = {
        vue: 'html', // Vue 文件通常使用 html 模式
        html: 'html',
        css: 'css',
        js: 'javascript',
        ts: 'typescript',
        jsx: 'javascript',
        tsx: 'typescript',
        md: 'markdown',
        json: 'json',
    };
    return languageMap[extension] || 'plaintext';
};
// ==================== 组件定义 ====================
export default defineComponent({
    name: 'AiPageDiff',
    props: {
        originalData: {
            type: Object,
            required: true,
        },
        modifiedData: {
            type: Object,
            required: true,
        },
        preTitle: {
            type: String,
            default: '',
        },
        nextTitle: {
            type: String,
            default: '',
        },
    },
    setup(props) {
        const { originalData, modifiedData } = toRefs(props);
        const mergeAiReResolveHandler = inject(MERGE_AI_RE_RESOLVE_HANDLER_KEY, undefined);
        // ==================== 状态管理 ====================
        const loading = ref(false);
        const error = ref('');
        const fileChanges = ref([]);
        const originFileTree = ref([]);
        const compareFileTree = ref([]);
        const selectedFile = ref(null);
        const viewMode = useStorage(VIEW_MODE_STORAGE_KEY, DEFAULT_VIEW_MODE);
        if (!isViewMode(viewMode.value)) {
            viewMode.value = DEFAULT_VIEW_MODE;
        }
        // Diff 展示模式（单栏/双栏），持久化到 localStorage
        const diffViewMode = useStorage(DIFF_VIEW_MODE_STORAGE_KEY, DEFAULT_DIFF_VIEW_MODE);
        if (![DiffViewMode.Unified, DiffViewMode.Split].includes(diffViewMode.value)) {
            diffViewMode.value = DEFAULT_DIFF_VIEW_MODE;
        }
        const toggleDiffViewMode = () => {
            diffViewMode.value = diffViewMode.value === DiffViewMode.Split
                ? DiffViewMode.Unified : DiffViewMode.Split;
        };
        // 左侧文件列表宽度（持久化到 localStorage，支持拖动调节）
        const fileListWidth = useStorage(FILE_LIST_WIDTH_STORAGE_KEY, FILE_LIST_DEFAULT_WIDTH);
        if (typeof fileListWidth.value !== 'number'
            || Number.isNaN(fileListWidth.value)
            || fileListWidth.value < FILE_LIST_MIN_WIDTH
            || fileListWidth.value > FILE_LIST_MAX_WIDTH) {
            fileListWidth.value = FILE_LIST_DEFAULT_WIDTH;
        }
        const isResizing = ref(false);
        // 文件目录折叠（对齐 AI 编辑器侧栏交互，持久化到 localStorage）
        const fileListCollapsed = useStorage(FILE_LIST_COLLAPSED_STORAGE_KEY, false);
        const toggleFileListCollapsed = () => {
            fileListCollapsed.value = !fileListCollapsed.value;
        };
        // 各文件夹展开/收起（默认展开；key 为目录 path / dirKey）
        const collapsedDirs = ref({});
        const isDirCollapsed = (dirPath) => Boolean(collapsedDirs.value[dirPath]);
        const toggleDirCollapsed = (dirPath) => {
            collapsedDirs.value = {
                ...collapsedDirs.value,
                [dirPath]: !collapsedDirs.value[dirPath],
            };
        };
        /** 选中文件时展开其祖先目录，避免文件被收在折叠夹里看不到 */
        const expandAncestorDirs = (filePath) => {
            const parts = filePath.split('/').filter(Boolean);
            if (parts.length <= 1) {
                return;
            }
            const next = { ...collapsedDirs.value };
            let acc = '';
            for (let i = 0; i < parts.length - 1; i++) {
                acc = acc ? `${acc}/${parts[i]}` : parts[i];
                delete next[acc];
            }
            collapsedDirs.value = next;
        };
        const handleResizerMousedown = (ev) => {
            ev.preventDefault();
            const startWidth = fileListWidth.value;
            isResizing.value = true;
            startMouseMove({
                initialEvent: ev,
                onMove: ({ deltaX }) => {
                    fileListWidth.value = Math.min(Math.max(startWidth + deltaX, FILE_LIST_MIN_WIDTH), FILE_LIST_MAX_WIDTH);
                },
                onEnd: () => {
                    isResizing.value = false;
                },
            });
        };
        // ==================== 计算属性 ====================
        const originalCommitId = computed(() => originalData.value?.mainBranch || '');
        const modifiedCommitId = computed(() => modifiedData.value?.mainBranch || '');
        /** 无文件 diff，但两侧 commit tip 不同（常见于仅元数据/提交记录变化） */
        const emptyChangesHint = computed(() => {
            if (fileChanges.value.length > 0) {
                return '';
            }
            if (originalCommitId.value
                && modifiedCommitId.value
                && originalCommitId.value !== modifiedCommitId.value) {
                return $t('k_1e6c906f');
            }
            return $t('k_f1f96a69');
        });
        const fileChangeMap = computed(() => {
            const m = new Map();
            fileChanges.value.forEach((f) => {
                m.set(f.path, f);
            });
            return m;
        });
        const unifiedFileTree = computed(() => mergeFileTrees(originFileTree.value, compareFileTree.value, fileChangeMap.value));
        const groupedFileChanges = computed(() => buildGroupedFileChanges(fileChanges.value));
        const diffEditorOptions = computed(() => ({
            renderSideBySide: diffViewMode.value === DiffViewMode.Split,
        }));
        // ==================== 方法 ====================
        /**
         * 加载并对比文件数据
         */
        const loadData = async () => {
            if (!originalCommitId.value && !modifiedCommitId.value) {
                loading.value = false;
                error.value = $t('k_ca71a54e');
                fileChanges.value = [];
                selectedFile.value = null;
                return;
            }
            // 合并场景常见：旧 tip 已有、新 tip 尚未回写。此时不要按「右侧空树」算出全部删除。
            if (originalCommitId.value && !modifiedCommitId.value) {
                loading.value = true;
                error.value = '';
                fileChanges.value = [];
                selectedFile.value = null;
                originFileTree.value = [];
                compareFileTree.value = [];
                return;
            }
            loading.value = true;
            error.value = '';
            try {
                const result = await getAIPageCodeByCommitId(originalCommitId.value, modifiedCommitId.value);
                originFileTree.value = result.originFiles ?? [];
                compareFileTree.value = result.compareFiles ?? [];
                const changes = diffFileTrees(result.originFiles, result.compareFiles);
                fileChanges.value = changes;
                // 默认选中第一个文件
                const [firstChange] = changes;
                if (firstChange) {
                    selectedFile.value = firstChange;
                }
                else {
                    selectedFile.value = null;
                }
            }
            catch (err) {
                console.error('Failed to load files:', err);
                error.value = $t('k_2e68944b');
            }
            finally {
                loading.value = false;
            }
        };
        /**
         * 处理文件选择事件
         */
        const handleFileClick = (file) => {
            expandAncestorDirs(file.path);
            selectedFile.value = file;
        };
        const setViewMode = (mode) => {
            if (!isViewMode(mode)) {
                viewMode.value = DEFAULT_VIEW_MODE;
                return;
            }
            viewMode.value = mode;
        };
        // commit tip 变化时重载（如 mergeSuccess 回写 projectInfo.mainBranch）
        watch([originalCommitId, modifiedCommitId], () => {
            loadData();
        }, { immediate: true });
        return {
            loading,
            error,
            fileChanges,
            emptyChangesHint,
            originFileTree,
            compareFileTree,
            selectedFile,
            viewMode,
            diffViewMode,
            toggleDiffViewMode,
            fileChangeMap,
            unifiedFileTree,
            groupedFileChanges,
            diffEditorOptions,
            handleFileClick,
            setViewMode,
            fileListWidth,
            isResizing,
            handleResizerMousedown,
            fileListCollapsed,
            toggleFileListCollapsed,
            isDirCollapsed,
            toggleDirCollapsed,
            mergeAiReResolveHandler,
        };
    },
    render() {
        // ==================== 渲染文件列表项（评审视图）====================
        const renderFileItem = (file) => {
            const isSelected = this.selectedFile?.path === file.path;
            return (<div key={file.path} class={{
                    'px-4 py-2 cursor-pointer transition-colors hover:bg-gray-50': true,
                    'bg-blue-50 hover:bg-blue-50': isSelected,
                }} onClick={() => this.handleFileClick(file)}>
          <div class="flex items-center gap-2">
            <div class={{
                    'flex-shrink-0 text-sm text-gray-900': true,
                    'font-semibold': isSelected,
                    'font-normal': !isSelected,
                }}>
              {file.name}
            </div>
            <div class="flex-1 min-w-0 text-xs text-gray-500 truncate">
              {getDirectoryPath(file.path)}
            </div>
            <div class="flex-shrink-0">
              {renderFileChangeIcon(file.type)}
            </div>
          </div>
        </div>);
        };
        // ==================== 树状视图 ====================
        // 缩进与图标常量（与 paddingLeft = BASE_PADDING + depth * INDENT 像素对齐）
        // 第 i 层祖先的图标中心 x 坐标 = BASE_PADDING + i * INDENT + ICON_HALF
        const BASE_PADDING = 12;
        const INDENT = 12;
        const ICON_HALF = 7; // Folder 图标宽度 14px 的一半
        // 仅高亮选中文件的「直接父文件夹」竖线，更上层的祖先不高亮
        const isAncestorHighlighted = (ancestorPath) => {
            const selectedPath = this.selectedFile?.path;
            if (!selectedPath) {
                return false;
            }
            const lastSlash = selectedPath.lastIndexOf('/');
            const parentDir = lastSlash >= 0 ? selectedPath.substring(0, lastSlash) : '';
            return ancestorPath === parentDir;
        };
        const renderGuides = (ancestorPaths) => ancestorPaths.map((ancestorPath, i) => {
            const highlighted = isAncestorHighlighted(ancestorPath);
            return (<div key={`v-${ancestorPath}`} class={{
                    'pointer-events-none absolute bottom-0 top-0 w-px transition-colors': true,
                    'bg-blue-400': highlighted,
                    'bg-gray-200': !highlighted,
                }} style={{ left: `${BASE_PADDING + i * INDENT + ICON_HALF}px` }}/>);
        });
        const renderTreeNodes = (nodes, depth, ancestorPaths) => nodes.map((node) => {
            const paddingLeft = BASE_PADDING + depth * INDENT;
            if (node.kind === 'directory') {
                const isSelectedParent = this.selectedFile?.path.startsWith(`${node.path}/`);
                const childAncestorPaths = [...ancestorPaths, node.path];
                const collapsed = this.isDirCollapsed(node.path);
                return (<div key={node.path}>
            <div class={{
                        'relative flex cursor-pointer items-center gap-1 py-1.5 pr-2 text-sm transition-colors hover:bg-gray-50': true,
                        'font-semibold text-gray-900': isSelectedParent,
                        'font-normal text-gray-600': !isSelectedParent,
                    }} style={{ paddingLeft: `${paddingLeft}px` }} title={collapsed ? $t('k_7b9a18b9') : $t('k_d27548a7')} onClick={() => this.toggleDirCollapsed(node.path)}>
              {renderGuides(ancestorPaths)}
              {collapsed ? (<ChevronRight size={14} class="shrink-0 text-gray-400" strokeWidth={2}/>) : (<ChevronDown size={14} class="shrink-0 text-gray-400" strokeWidth={2}/>)}
              <Folder size={14} class="shrink-0 text-gray-400" strokeWidth={2}/>
              <span class="min-w-0 flex-1 truncate" title={node.name}>{node.name}</span>
            </div>
            {!collapsed && renderTreeNodes(node.children, depth + 1, childAncestorPaths)}
          </div>);
            }
            const { change } = node;
            const hasChange = !!change;
            const isSelected = change && this.selectedFile?.path === node.path;
            return (<div key={node.path} class={{
                    'relative flex items-center gap-2 py-1.5 pr-2 text-sm transition-colors': true,
                    'cursor-pointer hover:bg-gray-50': hasChange,
                    'cursor-default text-gray-400': !hasChange,
                    'bg-blue-50 hover:bg-blue-50': hasChange && isSelected,
                }} style={{ paddingLeft: `${paddingLeft}px` }} onClick={() => {
                    if (change) {
                        this.handleFileClick(change);
                    }
                }}>
          {renderGuides(ancestorPaths)}
          <span class={{
                    'min-w-0 flex-1 truncate': true,
                    'text-gray-900': hasChange,
                    'font-normal': !hasChange,
                    'font-semibold': isSelected,
                }} title={node.name}>
            {node.name}
          </span>
          {hasChange && change ? (<div class="flex shrink-0 items-center gap-1">
              {renderFileChangeIcon(change.type)}
            </div>) : (<span class="w-4 shrink-0 text-[10px] text-gray-300">—</span>)}
        </div>);
        });
        const renderTreeView = () => (<div class="py-1">
        {renderTreeNodes(this.unifiedFileTree, 0, [])}
      </div>);
        // ==================== 列表视图（按路径分组）====================
        const renderListView = () => (<div class="divide-y divide-gray-100">
        {this.groupedFileChanges.map((group) => {
                const collapsed = this.isDirCollapsed(group.dirKey);
                return (<div key={group.dirKey}>
              <div class="flex cursor-pointer items-center gap-1.5 bg-gray-50/90 px-3 py-2 text-xs text-gray-500 transition-colors hover:bg-gray-100" title={collapsed ? $t('k_7b9a18b9') : $t('k_d27548a7')} onClick={() => this.toggleDirCollapsed(group.dirKey)}>
                {collapsed ? (<ChevronRight size={14} class="shrink-0 text-gray-400" strokeWidth={2}/>) : (<ChevronDown size={14} class="shrink-0 text-gray-400" strokeWidth={2}/>)}
                <Folder size={14} class="shrink-0 text-gray-400" strokeWidth={2}/>
                <span class="min-w-0 truncate">{group.dirLabel}</span>
              </div>
              {!collapsed && group.files.map((file) => {
                        const isSelected = this.selectedFile?.path === file.path;
                        return (<div key={file.path} class={{
                                'flex cursor-pointer items-center gap-2 py-2 pl-8 pr-4 text-sm transition-colors hover:bg-gray-50': true,
                                'bg-blue-50 hover:bg-blue-50': isSelected,
                            }} onClick={() => this.handleFileClick(file)}>
                    <span class={{
                                'min-w-0 flex-1 truncate': true,
                                'text-gray-900': file.type !== FileChangeType.Added,
                                'text-green-700': file.type === FileChangeType.Added,
                                'font-semibold': isSelected,
                                'font-normal': !isSelected,
                            }} title={file.name}>
                      {file.name}
                    </span>
                    <div class="flex shrink-0 items-center">
                      {renderFileChangeIcon(file.type)}
                    </div>
                  </div>);
                    })}
            </div>);
            })}
      </div>);
        // ==================== 视图切换下拉 ====================
        const renderViewDropdown = () => {
            const renderViewModeIcon = (mode, selected = false, size = 16) => {
                const iconClass = selected ? 'shrink-0 text-blue-600' : 'shrink-0 text-gray-500';
                if (mode === ViewMode.List) {
                    return <List size={size} class={iconClass} strokeWidth={2}/>;
                }
                if (mode === ViewMode.Tree) {
                    return <FolderTree size={size} class={iconClass} strokeWidth={2}/>;
                }
                return <Eye size={size} class={iconClass} strokeWidth={2}/>;
            };
            return (<t-dropdown trigger="click" minColumnWidth={108} maxColumnWidth={108} popupProps={{
                    overlayInnerStyle: {
                        padding: '2px',
                        border: 'none',
                        borderRadius: '6px',
                        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.12)',
                    },
                }}>
          <button type="button" class="relative flex h-7 w-7 shrink-0 cursor-pointer appearance-none items-center justify-center rounded border-0 bg-transparent p-0 text-gray-600 shadow-none outline-none hover:bg-gray-100 focus:outline-none" title={$t('k_532f379d')} aria-label={$t('k_3699bfd2')}>
            {renderViewModeIcon(this.viewMode, false, 18)}
          </button>
          <t-dropdown-menu>
            {VIEW_MODES.map(mode => (<t-dropdown-item key={mode} class="!p-0" onClick={(_, { e }) => {
                        e.stopPropagation();
                        this.setViewMode(mode);
                    }}>
                <div class="flex h-7 w-[104px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded px-1.5">
                  {renderViewModeIcon(mode, this.viewMode === mode, 15)}
                  <span class={{
                        'text-sm': true,
                        'text-blue-600': this.viewMode === mode,
                        'text-gray-800': this.viewMode !== mode,
                    }}>
                    {VIEW_MODE_LABEL[mode]}
                  </span>
                </div>
              </t-dropdown-item>))}
          </t-dropdown-menu>
        </t-dropdown>);
        };
        // ==================== 渲染文件列表内容 ====================
        const renderFileListContent = () => {
            if (this.loading) {
                return (<div class="py-12 text-center text-sm text-gray-400">
            {$t('k_877783df')}
          </div>);
            }
            if (this.error) {
                return (<div class="py-12 text-center text-sm text-red-500">
            {this.error}
          </div>);
            }
            if (this.fileChanges.length === 0) {
                return (<div class="py-12 text-center text-sm text-gray-400">
            {this.emptyChangesHint}
          </div>);
            }
            if (this.viewMode === ViewMode.Tree) {
                return renderTreeView();
            }
            if (this.viewMode === ViewMode.List) {
                return renderListView();
            }
            return (<div class="divide-y divide-gray-100">
          {this.fileChanges.map(file => renderFileItem(file))}
        </div>);
        };
        // ==================== 渲染左侧文件列表 ====================
        // 固定头 + 独立滚动列表，overscroll contain 避免滚轮传到右侧 Diff
        const renderFileList = () => (<div class="flex h-full min-h-0 shrink-0 flex-col bg-white" style={{ width: `${this.fileListWidth}px` }}>
        <div class="flex shrink-0 items-center justify-between gap-2 bg-gray-50 px-4 py-2">
          <div class="min-w-0 text-sm font-semibold text-gray-900">
            {$t('k_b1546e5a', { arg_0: this.fileChanges.length })}
          </div>
          {renderViewDropdown()}
        </div>
        <div class="min-h-0 flex-1 overflow-auto" style={{ overscrollBehavior: 'contain' }}>
          {renderFileListContent()}
        </div>
      </div>);
        // ==================== 拖动分隔条（含收起按钮，hover 显示，对齐 gogo editor） ====================
        const renderResizer = () => (<div class={{
                [styles.resizer]: true,
                [styles.resizing]: this.isResizing,
            }} title={$t('k_ee5ecc61')} onMousedown={this.handleResizerMousedown}>
        <button type="button" class={styles.collapseBtn} title={$t('k_e8b499ed')} aria-label={$t('k_e8b499ed')} onMousedown={(e) => e.stopPropagation()} onClick={(e) => {
                e.stopPropagation();
                this.toggleFileListCollapsed();
            }}>
          <ChevronLeft class="pointer-events-none" size={14}/>
        </button>
      </div>);
        // ==================== 折叠后常驻的展开按钮 ====================
        const renderExpandButton = () => (<button type="button" class={styles.expandBtn} title={$t('k_f7803405')} aria-label={$t('k_f7803405')} onClick={this.toggleFileListCollapsed}>
        <ChevronRight size={16}/>
      </button>);
        // ==================== 渲染右侧 Diff 展示 ====================
        const renderDiffView = () => {
            if (!this.selectedFile) {
                return (<div class="flex h-full items-center justify-center text-sm text-gray-400">
            {this.loading ? $t('k_877783df') : $t('k_63b38a6b')}
          </div>);
            }
            const isSplit = this.diffViewMode === DiffViewMode.Split;
            return (<div class="flex h-full min-h-0 flex-col">
          {/* 文件信息头部 */}
          <div class="shrink-0 border-b border-gray-200 bg-gray-50 px-4 py-2">
            <div class="flex items-center gap-2">
              {renderFileChangeIcon(this.selectedFile.type)}
              <div class="truncate text-sm font-semibold text-gray-900">
                {this.selectedFile.name}
              </div>
              <div class="min-w-0 flex-1 truncate text-xs text-gray-500">
                {this.selectedFile.path}
              </div>
              {this.mergeAiReResolveHandler && (<a-button type="primary" onClick={() => this.mergeAiReResolveHandler?.()}>
                  {$t('k_9da91dca')}
                </a-button>)}
              <button type="button" class="flex h-7 w-7 shrink-0 cursor-pointer appearance-none items-center justify-center rounded border-0 bg-transparent p-0 text-gray-600 shadow-none outline-none hover:bg-gray-100 focus:outline-none" title={isSplit ? $t('k_c68232f2') : $t('k_3467c439')} aria-label={isSplit ? $t('k_c68232f2') : $t('k_3467c439')} onClick={this.toggleDiffViewMode}>
                {isSplit ? (<SquareSplitHorizontal size={16} strokeWidth={2}/>) : (<AlignJustify size={16} strokeWidth={2}/>)}
              </button>
            </div>
          </div>

          {/* Diff 内容：独立滚动区域，不接收目录 overscroll；单栏隐藏双栏标题 */}
          <div class="min-h-0 flex-1 overflow-hidden" style={{ overscrollBehavior: 'contain' }}>
            <DetailDiffPro preTitle={this.preTitle || $t('k_ab4dad4a')} nextTitle={this.nextTitle || $t('k_d93e74f0')} hideHeader={!isSplit} originalData={this.selectedFile.originalContent || ''} modifiedData={this.selectedFile.modifiedContent || ''} type={getLanguageFromFileName(this.selectedFile.name)} readOnly={true} options={this.diffEditorOptions}/>
          </div>
        </div>);
        };
        // ==================== 主渲染 ====================
        // 根节点不设 overflow-hidden，避免裁切分隔条上的折叠按钮
        return (<div class="relative flex h-full min-h-0">
        {!this.fileListCollapsed && renderFileList()}
        {!this.fileListCollapsed && renderResizer()}
        <div class="min-h-0 min-w-0 flex-1 overflow-hidden bg-white" style={{ overscrollBehavior: 'contain' }}>
          {renderDiffView()}
        </div>
        {this.fileListCollapsed && renderExpandButton()}
      </div>);
    },
});
